/**
 * 進捗・ウォレットの永続化(docs/02_game_design.md §5.6-§5.7 / docs/04_data_model.md §2)。
 *
 * 本番のコイン・進捗の書込は **Cloud Functions 経由のみ**(CLAUDE.md ルール2)。
 * ここは Phase 1 のローカル実装で、`ProgressService` インターフェースは
 * `submitStageResult({stageId, difficulty, score, playLog})`(docs/04 §5.1)に
 * そのまま載せ替えられる形にしてある。Firebase 実装は後日 `FirebaseProgressService` として追加する。
 */

import {
  calcClearCoins,
  calcKpm,
  calcMissRate,
  calcStarBonusCoins,
  calcStars,
  TYPING_STAGE_RULES,
  type Difficulty,
  type TypingLanguage,
} from '@paltyping/engine';
import type { LanguageCode } from './languages';

/** サーバへ送るプレイログ要約(docs/03 §6・functions の PlayLog と同形)。 */
export interface PlayLog {
  /** 正解打鍵数(ミスを含まない)。 */
  readonly totalTyped: number;
  /** ミス打鍵数。 */
  readonly totalMiss: number;
  /** プレイ時間(ms)。 */
  readonly durationMs: number;
  /** 完了した単語数。 */
  readonly wordsCompleted: number;
}

/** ステージ×難易度の記録。 */
export interface StageProgress {
  readonly stars: Readonly<Partial<Record<Difficulty, number>>>;
  readonly bestScore: Readonly<Partial<Record<Difficulty, number>>>;
  readonly clearedAt?: number;
  /** 通常ロックを Functions 経由で手動解除したか。欠損は未解除。 */
  readonly manualUnlocked?: boolean;
  /** 母国語で星5を受け取った回数。既存データの欠損は0。 */
  readonly nativeFiveStarClears?: Readonly<Partial<Record<Difficulty, number>>>;
  /** 母国語ステージを卒業済みか。既存データの欠損はfalse。 */
  readonly graduated?: Readonly<Partial<Record<Difficulty, boolean>>>;
}

/** 進捗スナップショット。 */
export interface ProgressSnapshot {
  readonly version: number;
  readonly coins: number;
  readonly stages: Readonly<Record<string, StageProgress>>;
}

/** リザルト送信。将来 Functions の `submitStageResult` にそのまま渡す。 */
export interface StageResultSubmission {
  readonly stageId: string;
  readonly difficulty: Difficulty;
  /**
   * ステージの言語(`StageDefinition.language`)。
   * クリアコインの係数が言語別なので必ず渡す(docs/02 §5.7)。
   * サーバーは申告を受け取らず、自分のステージ定義から言語を引く。
   */
  readonly language: TypingLanguage;
  readonly score: number;
  /** ステージの完了条件を満たしたか(将来はサーバがステージ定義から再判定する)。 */
  readonly cleared: boolean;
  /** ステージ定義の星1〜5ボーナスコイン。 */
  readonly starCoins: readonly number[];
  /**
   * 装備アイテムのコイン獲得倍率(`resolveModifiers().coinMultiplier`)。
   * 将来サーバーは装備状態から自分で再計算する(クライアント申告は検証対象)。
   */
  readonly coinMultiplier?: number;
  /** デモ時の母国語。Firebase は保存済み設定を Functions が読むため送信しない。 */
  readonly firstLanguage?: LanguageCode;
  readonly playLog: PlayLog;
}

/**
 * サーバーに記録できたか。ローカル実装は常に `saved`。
 *
 * - `saved`   : サーバー(または本実装の保存先)に記録済み
 * - `pending` : 通信できず保留。あとで送り直す(星・コインはローカルには残っている)
 * - `rejected`: サーバーに拒否された。**記録は残らない**(ステージ解放も進まない)
 */
export type StageResultSaveStatus = 'saved' | 'pending' | 'rejected';

/** 送信結果(Functions の SubmitStageResultResponse 相当 + ローカル表示用の内訳)。 */
export interface StageResultReceipt {
  readonly stars: number;
  readonly previousStars: number;
  readonly newStars: number;
  readonly clearCoins: number;
  readonly starBonusCoins: number;
  readonly coinsEarned: number;
  readonly balance: number;
  readonly bestScore: number;
  readonly isNewBestScore: boolean;
  /** 省略時は `saved`(ローカル実装・テスト用フィクスチャ)。 */
  readonly saveStatus?: StageResultSaveStatus;
  /**
   * サーバーがくれた**がんばりポイント**(docs/12 §2 のリーグ軸)。
   *
   * `null` / 未設定 = ローカルモード・オフライン・拒否 → **リザルトに出さない**
   * (「0 ポイント」とも出さない。コインと同じ流儀)。
   */
  readonly effortEarned?: number | null;
  /** 母国語で星5を受け取った累計。 */
  readonly nativeFiveStarClears?: number;
  /** この難易度が卒業済みか。 */
  readonly graduated?: boolean;
  /**
   * サーバーが保存を拒否した機械可読な理由。
   * `recovery-required` / `result-id-required` は旧Webを更新する必要があるため、
   * リザルト画面で一般の保存失敗とは分けて案内する。
   */
  readonly reason?: 'stage-graduated' | 'recovery-required' | 'result-id-required';
}

/** 進捗の読み書き。ローカル実装と Firebase 実装を差し替えられるようにする。 */
export interface ProgressService {
  load(): Promise<ProgressSnapshot>;
  submitStageResult(input: StageResultSubmission): Promise<StageResultReceipt>;
  /**
   * 通常ロックを無料で解除する。Firebase では Functions の成功時だけ反映する。
   * 既存のテスト用実装との互換性のため、未対応実装では省略できる。
   */
  unlockStage?(stageId: string, difficulty: Difficulty): Promise<ProgressSnapshot>;
  /** 開始前にサーバー権威の解除・卒業状態を照会する。 */
  refreshStageAccess?(stageId: string, difficulty: Difficulty): Promise<ProgressSnapshot>;
  reset(): Promise<ProgressSnapshot>;
}

/** 開始前のサーバー照会に対応する進捗サービス。 */
export interface StageAccessProgressService extends ProgressService {
  refreshStageAccess?(stageId: string, difficulty: Difficulty): Promise<ProgressSnapshot>;
}

/**
 * サーバーから読み戻した「上書きする項目」(docs/04_data_model.md §5.1)。
 *
 * 星・ベストスコア・コイン残高は**サーバーが権威**なので、まるごと置き換える
 * (足し合わせない。別アカウントで入った子に前の子の星が残らないようにする)。
 */
export interface ProgressHydration {
  /** typing 通貨の残高(`wallets/{uid}.coins.typing`)。 */
  readonly coins: number;
  readonly stages: Readonly<Record<string, StageProgress>>;
}

/** Functions が確定したステージ結果を、表示キャッシュへ一度に反映する入力。 */
export interface ServerStageResult {
  readonly stars: number;
  readonly balance: number;
  readonly nativeFiveStarClears?: number;
  readonly graduated?: boolean;
}

/** 開始前照会・卒業拒否時に Functions から得るステージアクセス状態。 */
export interface StageAccess {
  readonly stageId: string;
  readonly manualUnlocked: boolean;
  readonly nativeFiveStarClears: Readonly<Partial<Record<Difficulty, number>>>;
  readonly graduated: Readonly<Partial<Record<Difficulty, boolean>>>;
}

/** 読み戻しを受け取れる進捗キャッシュ(`LocalProgressService` が満たす)。 */
export interface HydratableProgress {
  hydrate(input: ProgressHydration, canApply?: () => boolean): Promise<ProgressSnapshot>;
  /** UID 切替・読み戻し後に、メモリキャッシュを捨てる。 */
  reload(): void;
}

/** コインの増減口(ガチャの消費・重複変換)。 */
export class InsufficientCoinsError extends Error {
  constructor(
    readonly required: number,
    readonly balance: number,
  ) {
    super(`コインがたりません(ひつよう ${required} / もっている ${balance})`);
    this.name = 'InsufficientCoinsError';
  }
}

/**
 * ウォレット。ローカルでは進捗と同じ localStorage に載っているが、
 * 本番では Functions が `wallets/{uid}` をトランザクション更新する。
 * ガチャ側はこのインターフェースしか知らない。
 */
export interface CoinWallet {
  balance(): Promise<number>;
  /** 残高を増減して新しい残高を返す。マイナスになる場合は例外。 */
  applyCoinDelta(delta: number): Promise<number>;
}

export const EMPTY_PROGRESS: ProgressSnapshot = { version: 1, coins: 0, stages: {} };

const STORAGE_KEY = 'paltyping.progress.v1';

/** 難易度ごとの星。未プレイは 0。 */
export function starsOf(
  snapshot: ProgressSnapshot,
  stageId: string,
  difficulty: Difficulty,
): number {
  return snapshot.stages[stageId]?.stars[difficulty] ?? 0;
}

/** 難易度をまたいだ最高星数(アンロック判定用)。 */
export function bestStarsOf(snapshot: ProgressSnapshot, stageId: string): number {
  const stars = snapshot.stages[stageId]?.stars;
  if (!stars) return 0;
  const values = Object.values(stars).filter((value): value is number => typeof value === 'number');
  return values.length === 0 ? 0 : Math.max(0, ...values);
}

/** 難易度ごとのベストスコア。 */
export function bestScoreOf(
  snapshot: ProgressSnapshot,
  stageId: string,
  difficulty: Difficulty,
): number {
  return snapshot.stages[stageId]?.bestScore[difficulty] ?? 0;
}

/**
 * リザルトを進捗に適用する(純関数)。
 *
 * - 星: `calcStars`(engine)で playLog から再計算。星は単調増加で記録する
 * - コイン: `min(floor(score × 言語別係数), 500)` + 初回星ボーナス(docs/02 §5.7)
 */
export function applyStageResult(
  snapshot: ProgressSnapshot,
  input: StageResultSubmission,
  nowMs: number,
): { readonly snapshot: ProgressSnapshot; readonly receipt: StageResultReceipt } {
  const { totalTyped, totalMiss, durationMs } = input.playLog;
  const missRate = calcMissRate(totalTyped, totalMiss);
  const kpm = calcKpm(totalTyped, durationMs);
  const stars = calcStars({ cleared: input.cleared, missRate, kpm });

  const previousStars = starsOf(snapshot, input.stageId, input.difficulty);
  const previousBest = bestScoreOf(snapshot, input.stageId, input.difficulty);

  const clearCoins = input.cleared
    ? calcClearCoins(input.score, input.language, input.coinMultiplier ?? 1)
    : 0;
  const starBonusCoins = calcStarBonusCoins(stars, previousStars, input.starCoins);
  const coinsEarned = clearCoins + starBonusCoins;

  const nextStars = Math.max(previousStars, stars);
  const bestScore = Math.max(previousBest, input.score);
  const isNewBestScore = input.score > previousBest;

  const existing = snapshot.stages[input.stageId];
  const stageProgress: StageProgress = {
    stars: { ...existing?.stars, [input.difficulty]: nextStars },
    bestScore: { ...existing?.bestScore, [input.difficulty]: bestScore },
    ...(input.cleared || existing?.clearedAt !== undefined
      ? { clearedAt: existing?.clearedAt ?? nowMs }
      : {}),
    ...(existing?.manualUnlocked === true ? { manualUnlocked: true } : {}),
    ...(existing?.nativeFiveStarClears
      ? { nativeFiveStarClears: existing.nativeFiveStarClears }
      : {}),
    ...(existing?.graduated ? { graduated: existing.graduated } : {}),
  };

  const nextSnapshot: ProgressSnapshot = {
    version: snapshot.version,
    coins: snapshot.coins + coinsEarned,
    stages: { ...snapshot.stages, [input.stageId]: stageProgress },
  };

  return {
    snapshot: nextSnapshot,
    receipt: {
      stars,
      previousStars,
      newStars: Math.max(0, stars - previousStars),
      clearCoins,
      starBonusCoins,
      coinsEarned,
      balance: nextSnapshot.coins,
      bestScore,
      isNewBestScore,
    },
  };
}

/** localStorage 互換の最小インターフェース(テストで差し替えるため)。 */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** 壊れたJSON・古いスキーマを捨てて既定値に落とす。 */
export function deserializeProgress(raw: string | null): ProgressSnapshot {
  if (!raw) return EMPTY_PROGRESS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_PROGRESS;
    const record = parsed as Partial<ProgressSnapshot>;
    if (record.version !== 1) return EMPTY_PROGRESS;
    return {
      version: 1,
      coins: typeof record.coins === 'number' && record.coins >= 0 ? Math.floor(record.coins) : 0,
      stages: typeof record.stages === 'object' && record.stages !== null ? record.stages : {},
    };
  } catch {
    return EMPTY_PROGRESS;
  }
}

/**
 * localStorage 実装。
 * Phase 2 で `FirebaseProgressService`(callable `submitStageResult`)に差し替える。
 */
export class LocalProgressService implements ProgressService, CoinWallet, HydratableProgress {
  private snapshot: ProgressSnapshot | null = null;
  /** await を含む更新が同じ古い snapshot を元に保存しないための短い直列化キュー。 */
  private mutations: Promise<void> = Promise.resolve();

  constructor(
    private readonly storage: KeyValueStorage | null = safeLocalStorage(),
    private readonly key: string = STORAGE_KEY,
    private readonly now: () => number = () => Date.now(),
  ) {}

  reload(): void {
    this.snapshot = null;
  }

  async load(): Promise<ProgressSnapshot> {
    if (!this.snapshot) {
      this.snapshot = deserializeProgress(this.storage?.getItem(this.key) ?? null);
    }
    return this.snapshot;
  }

  async submitStageResult(
    input: StageResultSubmission,
    canApply: () => boolean = () => true,
  ): Promise<StageResultReceipt> {
    return this.mutate(async () => {
      const current = await this.load();
      // Firebase の楽観反映は UID 所有者をここまで保持する。load() が待機した間に
      // 切り替わっていれば、別の子の appStorage へ書き込ませない。
      if (!canApply()) throw new Error('アカウントが切り替わりました');
      const existing = current.stages[input.stageId];
      if (existing?.graduated?.[input.difficulty]) {
        return {
          ...applyStageResult(current, input, this.now()).receipt,
          saveStatus: 'rejected',
          reason: 'stage-graduated',
          clearCoins: 0,
          starBonusCoins: 0,
          coinsEarned: 0,
          newStars: 0,
          balance: current.coins,
          isNewBestScore: false,
          nativeFiveStarClears: existing.nativeFiveStarClears?.[input.difficulty] ?? 0,
          graduated: true,
        };
      }
      const { snapshot, receipt } = applyStageResult(current, input, this.now());
      const earnedStars = receipt.stars;
      const isNativeFiveStar =
        input.firstLanguage !== undefined &&
        isFirstLanguageForTyping(input.firstLanguage, input.language) &&
        earnedStars === 5;
      const priorCount = existing?.nativeFiveStarClears?.[input.difficulty] ?? 0;
      const nativeFiveStarClears = isNativeFiveStar ? priorCount + 1 : priorCount;
      const graduated = nativeFiveStarClears >= TYPING_STAGE_RULES.nativeFiveStarClearsToGraduate;
      const nativeStage: StageProgress | undefined = isNativeFiveStar
        ? {
            ...(snapshot.stages[input.stageId] ?? { stars: {}, bestScore: {} }),
            nativeFiveStarClears: {
              ...existing?.nativeFiveStarClears,
              [input.difficulty]: nativeFiveStarClears,
            },
            ...(graduated
              ? { graduated: { ...existing?.graduated, [input.difficulty]: true } }
              : {}),
          }
        : undefined;
      this.snapshot = isNativeFiveStar
        ? {
            ...snapshot,
            stages: {
              ...snapshot.stages,
              [input.stageId]: nativeStage!,
            },
          }
        : snapshot;
      this.persist();
      return {
        ...receipt,
        ...(isNativeFiveStar ? { nativeFiveStarClears, graduated } : {}),
      };
    });
  }

  /** ローカル/デモ用。通常ロック解除をステージ単位で表示キャッシュに残す。 */
  async unlockStage(
    stageId: string,
    difficulty: Difficulty,
    canApply: () => boolean = () => true,
  ): Promise<ProgressSnapshot> {
    return this.mutate(async () => {
      const current = await this.load();
      // Firebase 実装は応答後に UID が変わっていないことをここでも渡す。
      // `load()` は await なので、この直前で確認しないと別の子のキャッシュを更新し得る。
      if (!canApply()) throw new Error('アカウントが切り替わりました');
      const existing = current.stages[stageId] ?? { stars: {}, bestScore: {} };
      if (existing.graduated?.[difficulty]) {
        throw new Error('卒業済みの難易度は手動で解除できません');
      }
      this.snapshot = {
        ...current,
        stages: {
          ...current.stages,
          [stageId]: { ...existing, manualUnlocked: true },
        },
      };
      this.persist();
      return this.snapshot;
    });
  }

  /** ローカル/デモではキャッシュだけを返す。Firebase は下の合流口を使う。 */
  async refreshStageAccess(_stageId: string, _difficulty: Difficulty): Promise<ProgressSnapshot> {
    return this.load();
  }

  /**
   * Functions のアクセス照会・卒業拒否を、現在のキャッシュへ単調に合流する。
   * 個別応答は到着順が前後するため、count は max、真偽値は OR でしか変えない。
   */
  async applyStageAccess(
    access: StageAccess,
    canApply: () => boolean = () => true,
  ): Promise<ProgressSnapshot> {
    return this.mutate(async () => {
      const current = await this.load();
      if (!canApply()) throw new Error('アカウントが切り替わりました');
      const existing = current.stages[access.stageId] ?? { stars: {}, bestScore: {} };
      this.snapshot = {
        ...current,
        stages: {
          ...current.stages,
          [access.stageId]: {
            ...existing,
            ...(access.manualUnlocked || existing.manualUnlocked ? { manualUnlocked: true } : {}),
            nativeFiveStarClears: mergeCounts(
              existing.nativeFiveStarClears,
              access.nativeFiveStarClears,
            ),
            graduated: mergeFlags(existing.graduated, access.graduated),
          },
        },
      };
      this.persist();
      return this.snapshot;
    });
  }

  /** Functions が確定した星・残高・卒業状態を一度の保存で反映する。 */
  async applyServerStageResult(
    input: Pick<StageResultSubmission, 'stageId' | 'difficulty' | 'score'>,
    result: ServerStageResult,
    canApply: () => boolean = () => true,
  ): Promise<ProgressSnapshot> {
    return this.mutate(async () => {
      const current = await this.load();
      if (!canApply()) throw new Error('アカウントが切り替わりました');
      const existing = current.stages[input.stageId] ?? { stars: {}, bestScore: {} };
      const serverStars = safeNonNegativeInt(result.stars);
      const serverCount = safeNonNegativeInt(result.nativeFiveStarClears);
      this.snapshot = {
        ...current,
        coins: safeNonNegativeInt(result.balance),
        stages: {
          ...current.stages,
          [input.stageId]: {
            ...existing,
            stars: {
              ...existing.stars,
              [input.difficulty]: Math.max(existing.stars[input.difficulty] ?? 0, serverStars),
            },
            bestScore: {
              ...existing.bestScore,
              [input.difficulty]: Math.max(existing.bestScore[input.difficulty] ?? 0, input.score),
            },
            ...(serverStars > 0 || existing.clearedAt !== undefined
              ? { clearedAt: existing.clearedAt ?? this.now() }
              : {}),
            ...(existing.manualUnlocked ? { manualUnlocked: true } : {}),
            ...(result.nativeFiveStarClears === undefined
              ? existing.nativeFiveStarClears
                ? { nativeFiveStarClears: existing.nativeFiveStarClears }
                : {}
              : {
                  nativeFiveStarClears: mergeCounts(existing.nativeFiveStarClears, {
                    [input.difficulty]: serverCount,
                  }),
                }),
            ...(result.graduated === undefined
              ? existing.graduated
                ? { graduated: existing.graduated }
                : {}
              : {
                  graduated: mergeFlags(existing.graduated, {
                    [input.difficulty]: result.graduated,
                  }),
                }),
          },
        },
      };
      this.persist();
      return this.snapshot;
    });
  }

  async balance(): Promise<number> {
    return (await this.load()).coins;
  }

  async applyCoinDelta(delta: number): Promise<number> {
    return this.mutate(async () => {
      const current = await this.load();
      const next = current.coins + delta;
      if (next < 0) throw new InsufficientCoinsError(-delta, current.coins);
      this.snapshot = { ...current, coins: next };
      this.persist();
      return next;
    });
  }

  /**
   * サーバー権威の値で上書きする(docs/04 §5.1 の読み戻し)。
   *
   * 星・ベストスコア・コインは**サーバーが正**。ローカルにしか無いもの
   * (設定・打鍵統計)はこのキャッシュに入っていないので、ここでは何も失われない。
   */
  async hydrate(
    input: ProgressHydration,
    canApply: () => boolean = () => true,
  ): Promise<ProgressSnapshot> {
    return this.mutate(async () => {
      const current = await this.load();
      if (!canApply()) throw new Error('アカウントが切り替わりました');
      this.snapshot = {
        version: current.version,
        coins: Math.max(0, Math.floor(input.coins)),
        stages: input.stages,
      };
      this.persist();
      return this.snapshot;
    });
  }

  async reset(): Promise<ProgressSnapshot> {
    return this.mutate(async () => {
      this.snapshot = EMPTY_PROGRESS;
      this.storage?.removeItem(this.key);
      return this.snapshot;
    });
  }

  private persist(): void {
    if (!this.storage || !this.snapshot) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(this.snapshot));
    } catch {
      // 容量超過・プライベートモード等。進捗が消えるだけでプレイは続行できる。
    }
  }

  private async mutate<T>(operation: () => Promise<T>): Promise<T> {
    const previous = this.mutations;
    let release!: () => void;
    this.mutations = new Promise<void>((resolve) => {
      release = resolve;
    });
    await previous;
    try {
      return await operation();
    } finally {
      release();
    }
  }
}

function safeLocalStorage(): KeyValueStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function safeNonNegativeInt(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function isFirstLanguageForTyping(firstLanguage: LanguageCode, language: TypingLanguage): boolean {
  return (
    (firstLanguage === 'ja' && language === 'japanese') ||
    (firstLanguage === 'en' && language === 'english')
  );
}

function mergeCounts(
  current: Readonly<Partial<Record<Difficulty, number>>> | undefined,
  incoming: Readonly<Partial<Record<Difficulty, number>>>,
): Partial<Record<Difficulty, number>> {
  const out: Partial<Record<Difficulty, number>> = { ...current };
  for (const difficulty of ['easy', 'normal', 'hard'] as const) {
    const value = incoming[difficulty];
    if (value === undefined) continue;
    out[difficulty] = Math.max(current?.[difficulty] ?? 0, safeNonNegativeInt(value));
  }
  return out;
}

function mergeFlags(
  current: Readonly<Partial<Record<Difficulty, boolean>>> | undefined,
  incoming: Readonly<Partial<Record<Difficulty, boolean>>>,
): Partial<Record<Difficulty, boolean>> {
  const out: Partial<Record<Difficulty, boolean>> = { ...current };
  for (const difficulty of ['easy', 'normal', 'hard'] as const) {
    if (incoming[difficulty] === true || current?.[difficulty] === true) out[difficulty] = true;
  }
  return out;
}
